import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, User, Phone, Mail, MapPin, FileText, Calculator, AlertTriangle, Calendar, Euro, ShieldCheck, Edit, Download, Camera } from 'lucide-react';
import ModalCrearAviso from '../componentes/ModalCrearAviso';
import ModalVerImagenes from '../componentes/ModalVerImagenes';
import ModalEditarDatosCliente from '../componentes/gestion-clientes/ModalEditarDatosCliente';
import { buscarClienteDetalles, exportarAvisosCsv } from '../servicios/api';

const BuscarClientePage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeTab, setActiveTab] = useState('datos');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [garantiaAbierta, setGarantiaAbierta] = useState(false);
  const [datosGarantia, setDatosGarantia] = useState(null);
  const [editarClienteAbierto, setEditarClienteAbierto] = useState(false);
  const [avisoConImagenes, setAvisoConImagenes] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const terminoFromUrl = params.get('termino');
    if (terminoFromUrl) {
      setSearchTerm(terminoFromUrl);
      handleSearch(terminoFromUrl);
    }
  }, [location.search]);

  const handleSearch = async (term) => {
    const searchTermToUse = term || searchTerm;
    if (!searchTermToUse.trim()) {
      setSelectedCustomer(null);
      setError('');
      return;
    }

    setLoading(true);
    setError('');
    setSelectedCustomer(null);
    try {
      const data = await buscarClienteDetalles(searchTermToUse.trim());

      if (data.exito && data.cliente) {
        setSelectedCustomer(data.cliente);
        setActiveTab('datos');
      } else {
        setError(data.mensaje || 'No se encontró ningún cliente con los datos proporcionados.');
      }
    } catch (err) {
      setError(err.response?.data?.mensaje || 'Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  const abrirGarantia = (aviso) => {
    setDatosGarantia({
      ...selectedCustomer,
      ...aviso,
      nombre: selectedCustomer.nombre,
      dni: selectedCustomer.dni || selectedCustomer.nie || '',
      email: selectedCustomer.email || '',
      aviso_origen_id: aviso.id_aviso || aviso.id,
    });
    setGarantiaAbierta(true);
  };

  const actualizarClienteEnVista = (clienteActualizado) => {
    setSelectedCustomer((clienteActual) => ({
      ...clienteActual,
      ...clienteActualizado,
      nombre: clienteActualizado.nombre,
      telefono: clienteActualizado.telefono,
      direccion: clienteActualizado.direccion,
      email: clienteActualizado.email,
      nif: clienteActualizado.nif,
      nie: clienteActualizado.nif,
      dni: clienteActualizado.nif,
    }));
  };

  const exportarHistorialCliente = async () => {
    try {
      await exportarAvisosCsv({ cliente_id: selectedCustomer.id_cliente });
    } catch (exportError) {
      setError(exportError.response?.data?.mensaje || 'No se pudo exportar el historial del cliente.');
    }
  };

  const getTipoRegistroLabel = (tipoRegistro) => ({
    diagnostico: 'Diagnóstico',
    reparacion: 'Reparación',
    garantia: 'Garantía',
  }[tipoRegistro] || 'Servicio');

  const getEstadoAvisoLabel = (estado) => ({
    pendiente: 'Pendiente',
    programado: 'En proceso',
    completado: 'Completado',
    documentos_enviados: 'Completado',
    resuelto: 'Completado',
    anulado: 'Anulado',
  }[estado] || estado || 'Sin estado');

  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'pagada':
      case 'completado':
      case 'aceptado':
        return 'text-green-300 bg-green-500/20';
      case 'pendiente':
        return 'text-orange-300 bg-orange-500/20';
      case 'cancelado':
      case 'rechazado':
        return 'text-red-400 bg-red-500/20';
      case 'programado':
        return 'text-blue-400 bg-blue-500/20';
      case 'documentos_enviados':
        return 'text-purple-300 bg-purple-500/20';
      default:
        return 'text-slate-400 bg-slate-700';
    }
  };

  const getTipoAvisoColor = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'urgente': return 'text-red-400 bg-red-500/20';
      case 'recordatorio': return 'text-blue-400 bg-blue-500/20';
      case 'información': return 'text-green-300 bg-green-500/20';
      default: return 'text-slate-400 bg-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 p-3 sm:p-6 text-slate-200">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-6 sm:mb-8">Búsqueda de Clientes</h1>
        
        <div className="bg-slate-800 rounded-lg shadow-md p-4 sm:p-6 mb-6 border border-slate-700">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Buscar por teléfono o dirección
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Teléfono o dirección del cliente"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchTerm)}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => handleSearch(searchTerm)}
                  disabled={loading}
                  className="w-full sm:w-auto px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2 font-medium disabled:bg-orange-800 disabled:cursor-not-allowed"
                >
                  <Search size={20} />
                  {loading ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
            </div>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-300 rounded-lg shadow-md p-4 sm:p-6 text-center">
            <p>{error}</p>
          </div>
        )}

        {selectedCustomer && (
          <div className="bg-slate-800 rounded-lg shadow-md overflow-hidden border border-slate-700">
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-4 sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-orange-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-2xl font-bold truncate">{selectedCustomer.nombre}</h2>
                  <p className="text-slate-300 text-sm sm:text-base">NIE: {selectedCustomer.nie}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <button type="button" onClick={exportarHistorialCliente} className="inline-flex items-center gap-2 rounded border border-slate-500 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700">
                    <Download size={16} /> CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditarClienteAbierto(true)}
                    className="inline-flex items-center gap-2 rounded border border-slate-500 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700"
                  >
                    <Edit size={16} />
                    Editar
                  </button>
                </div>
              </div>
            </div>

            <div className="border-b border-slate-700 overflow-x-auto">
              <nav className="flex min-w-max">
                {[
                  { key: 'datos', label: 'Datos', icon: User },
                  { key: 'facturas', label: 'Facturas', icon: FileText },
                  { key: 'presupuestos', label: 'Presupuestos', icon: Calculator },
                  { key: 'avisos', label: 'Avisos', icon: AlertTriangle }
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                      activeTab === key
                        ? 'border-orange-500 text-orange-400'
                        : 'border-transparent text-slate-400 hover:text-slate-200'
                    }`}>
                    <Icon size={16} />
                    <span>{label}</span>
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-4 sm:p-6">
              {activeTab === 'datos' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Phone className="text-slate-400 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <p className="font-medium text-sm sm:text-base text-slate-300">Teléfono</p>
                        <p className="text-slate-400 text-sm sm:text-base break-all">{selectedCustomer.telefono}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="text-slate-400 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <p className="font-medium text-sm sm:text-base text-slate-300">Email</p>
                        <p className="text-orange-400 text-sm sm:text-base break-all">{selectedCustomer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="text-slate-400 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <p className="font-medium text-sm sm:text-base text-slate-300">Dirección</p>
                        <p className="text-slate-400 text-sm sm:text-base">{selectedCustomer.direccion}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="text-slate-400 mt-1 flex-shrink-0" size={18} />
                      <div>
                        <p className="font-medium text-sm sm:text-base text-slate-300">Fecha de Registro</p>
                        <p className="text-slate-400 text-sm sm:text-base">{selectedCustomer.fechaRegistro}</p>
                      </div>
                    </div>
                    <div className="bg-slate-900/70 p-4 rounded-lg">
                      <h3 className="font-medium mb-3 text-sm sm:text-base text-slate-200">Resumen de Actividad</h3>
                      <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                        <div>
                          <p className="text-xl sm:text-2xl font-bold text-orange-400">{selectedCustomer.facturas?.length || 0}</p>
                          <p className="text-xs sm:text-sm text-slate-400">Facturas</p>
                        </div>
                        <div>
                          <p className="text-xl sm:text-2xl font-bold text-green-400">{selectedCustomer.presupuestos?.length || 0}</p>
                          <p className="text-xs sm:text-sm text-slate-400">Presupuestos</p>
                        </div>
                        <div>
                          <p className="text-xl sm:text-2xl font-bold text-blue-400">{selectedCustomer.avisos?.length || 0}</p>
                          <p className="text-xs sm:text-sm text-slate-400">Avisos</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'facturas' && (
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-200">Facturas del Cliente</h3>
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 font-semibold text-sm text-slate-300">N° Factura</th>
                          <th className="text-left p-3 font-semibold text-sm text-slate-300">Fecha</th>
                          <th className="text-left p-3 font-semibold text-sm text-slate-300">Concepto</th>
                          <th className="text-right p-3 font-semibold text-sm text-slate-300">Importe</th>
                          <th className="text-center p-3 font-semibold text-sm text-slate-300">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCustomer.facturas?.map((factura) => (
                          <tr key={factura.id} className="hover:bg-slate-700/50">
                            <td className="p-3 border-b border-slate-700 font-medium text-sm">{factura.id}</td>
                            <td className="p-3 border-b border-slate-700 text-sm">{factura.fecha}</td>
                            <td className="p-3 border-b border-slate-700 text-sm">{factura.concepto}</td>
                            <td className="p-3 border-b border-slate-700 text-right font-medium text-sm">
                              <span className="flex items-center justify-end gap-1"><Euro size={14} />{factura.importe.toFixed(2)}</span>
                            </td>
                            <td className="p-3 border-b border-slate-700 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(factura.estado)}`}>{factura.estado}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'presupuestos' && (
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-200">Presupuestos del Cliente</h3>
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-3 font-semibold text-sm text-slate-300">N° Presupuesto</th>
                          <th className="text-left p-3 font-semibold text-sm text-slate-300">Fecha</th>
                          <th className="text-left p-3 font-semibold text-sm text-slate-300">Concepto</th>
                          <th className="text-right p-3 font-semibold text-sm text-slate-300">Importe</th>
                          <th className="text-center p-3 font-semibold text-sm text-slate-300">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCustomer.presupuestos?.map((presupuesto) => (
                          <tr key={presupuesto.id} className="hover:bg-slate-700/50">
                            <td className="p-3 border-b border-slate-700 font-medium text-sm">{presupuesto.id}</td>
                            <td className="p-3 border-b border-slate-700 text-sm">{presupuesto.fecha}</td>
                            <td className="p-3 border-b border-slate-700 text-sm">{presupuesto.concepto}</td>
                            <td className="p-3 border-b border-slate-700 text-right font-medium text-sm">
                              <span className="flex items-center justify-end gap-1"><Euro size={14} />{presupuesto.importe.toFixed(2)}</span>
                            </td>
                            <td className="p-3 border-b border-slate-700 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(presupuesto.estado)}`}>{presupuesto.estado}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === 'avisos' && (
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-slate-200">Avisos del Cliente</h3>
                  <div className="space-y-3">
                    {selectedCustomer.avisos?.map((aviso) => (
                      <div key={aviso.id} className="border border-slate-700 rounded-lg p-3 sm:p-4 hover:bg-slate-700/50">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="text-slate-400 mt-1 flex-shrink-0" size={16} />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="text-xs sm:text-sm font-semibold text-blue-300">
                                Aviso #{aviso.numero_aviso ?? aviso.id_aviso}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium w-fit ${getEstadoColor(aviso.estado)}`}>{getEstadoAvisoLabel(aviso.estado)}</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${aviso.tipo_registro === 'garantia' ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700 text-slate-200'}`}>
                                {getTipoRegistroLabel(aviso.tipo_registro)}
                              </span>
                              <span className="text-xs sm:text-sm text-slate-400">
                                {new Date(aviso.fecha_cita ? `${aviso.fecha_cita}T${aviso.hora_cita || '00:00:00'}` : aviso.fecha).toLocaleString('es-ES')}
                              </span>
                            </div>
                            {aviso.aparato && <p className="mb-1 text-sm font-medium text-slate-200">Aparato: {aviso.aparato}</p>}
                            <p className="text-slate-300 text-sm sm:text-base">{aviso.mensaje}</p>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              {aviso.imagenes?.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => setAvisoConImagenes(aviso)}
                                  className="inline-flex items-center gap-2 rounded border border-blue-500/60 px-3 py-2 text-sm font-medium text-blue-300 hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <Camera size={16} />
                                  Ver fotos de este servicio ({aviso.imagenes.length})
                                </button>
                              ) : (
                                <span className="text-xs text-slate-500">Este servicio no tiene fotos adjuntas.</span>
                              )}
                              {['reparacion', 'diagnostico'].includes(aviso.tipo_registro) && ['completado', 'documentos_enviados'].includes(aviso.estado) && (
                                <button
                                  type="button"
                                  onClick={() => abrirGarantia(aviso)}
                                  className="inline-flex items-center gap-2 rounded border border-orange-500/60 px-3 py-2 text-sm font-medium text-orange-300 hover:bg-orange-500/10 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                >
                                  <ShieldCheck size={16} />
                                  Programar Garantía
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ModalCrearAviso
        isOpen={garantiaAbierta}
        onClose={() => setGarantiaAbierta(false)}
        onAvisoCreated={() => handleSearch(searchTerm)}
        initialData={datosGarantia}
      />
      <ModalVerImagenes
        isOpen={Boolean(avisoConImagenes)}
        onClose={() => setAvisoConImagenes(null)}
        imagenes={avisoConImagenes?.imagenes || []}
        titulo={avisoConImagenes
          ? `Fotos del servicio #${avisoConImagenes.numero_aviso ?? avisoConImagenes.id_aviso}`
          : undefined}
      />
      <ModalEditarDatosCliente
        isOpen={editarClienteAbierto}
        cliente={selectedCustomer}
        onClose={() => setEditarClienteAbierto(false)}
        onSaved={actualizarClienteEnVista}
      />
    </div>
  );
};

export default BuscarClientePage;
